/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 이미지블록에서 쓰이는 아이템 블록을 정의한다.
 *
 * @file /src/rootin/blocks/images/blocks/Item.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Image } from '@rootin/elements/image/Image';
import { Language, LanguageText } from '@utils/Language';
import './Item.css';

export interface Properties {}

export class Item extends Block {
    /**
     * 블록타입
     */
    static type = 'images-item';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string = 'mi mi-image';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.images.blocks.item.label');

    /**
     * 이미지 엘리먼트
     */
    image: Image;

    /**
     * 이미지블록에서 쓰이는 아이템 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.anchor.setAnchorAlways(true);

        this.image = new Image(this, { name: 'image' });
        this.$getBlock().append(this.image.$getElement());
    }

    /**
     * 드래그 중인 파일을 블록이 받을 수 있는지 판단한다.
     *
     * @param fileType - 파일의 MIME 타입
     * @returns 판단여부 (insert : 블록추가 및 파일드롭 허용, drop : 파일드롭만 허용, false : 파일드롭을 서용하지 않음)
     */
    static acceptFileDrop(fileType: string): 'insert' | 'drop' | false {
        return fileType.startsWith('image/') ? 'drop' : false;
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.image.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.image.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), image: this.image.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.image.setValue(value.image);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $image = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'image'
        );
        if ($image === undefined) {
            return;
        }

        const attachmentId = $image.getAttribute('data-attachment-id');
        await this.image.setValue({
            attachment_id: attachmentId?.length === 36 ? attachmentId : null,
            alt: $image.getAttribute('data-alt') ?? '',
        });
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     * 이미지는 뽑아낼 텍스트가 없으므로, 선택영역이 이 블록에 닿아있으면 항상 통째로 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        const $el = this.image.$getElement().getEl();
        if (range.intersectsNode($el) === false) {
            return { texts: [], remain: null };
        }

        await this.getParent()?.removeBlock(this);

        return { texts: [], remain: null };
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.anchor.focus();
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.anchor.focus();
        }
    }

    /**
     * 블록이 포커스를 받을 가능성이 있을 때 이벤트를 처리한다.
     */
    onFocusable(): void {
        this.getParent()?.block.focus(null);
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다. anchor(이 블록의 유일한
     * 캐럿정지점)에 포커스가 있는 상태로 Enter 를 누르면 업로드 창을 띄운다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        this.editor.focus.suspend();
        this.image.upload();
    }

    /**
     * 파일이 드롭되었을 때 이벤트를 처리한다.
     *
     * @param file - 드롭된 파일
     */
    async onFileDrop(file: File): Promise<void> {
        this.editor.focus.suspend();
        await this.image.upload(file);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (focused === null || focused instanceof Element) {
            return data;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        if (type === 'Files') {
            const file = (data as File[])[0] ?? null;
            if (file === null || file.type.startsWith('image/') === false) {
                return data;
            }

            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
            await this.onFileDrop(file);

            return true;
        }

        if (type !== 'text/html') {
            return data;
        }

        const dom = document.createElement('div');
        dom.innerHTML = data as string;

        const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
        if ($matched === null) {
            return data;
        }

        $matched.remove();
        const remain = dom.innerHTML === '' ? true : dom.innerHTML;

        await this.setValueFromHtml($matched.outerHTML);
        await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

        return remain;
    }
}
